import {
  BoxGeometry,
  Color,
  CylinderGeometry,
  DirectionalLight,
  Group,
  HemisphereLight,
  Mesh,
  Object3D,
  PerspectiveCamera,
  Scene,
  Vector3,
  type BufferGeometry,
  type Texture,
  type Material,
} from 'three';
import { surface, tintVertexColor } from '../render/materials';
import { lootBody } from '../world/loot-props';
import type { ItemId } from '../sim/types';

/**
 * YOUR HANDS.
 *
 * The single largest thing separating this from a demo was that the player was
 * a floating camera. You could hold a crowbar, cut a shutter with a grinder and
 * walk out with forty thousand dollars of watches, and see none of it — the
 * whole inventory existed as two lines of text in a corner. "I picked something
 * up and I cannot see it in my hand" was the report, and it is really a report
 * about presence: a first-person game with nothing in the frame that belongs to
 * your body has no body, and every other thing in it becomes scenery you are
 * being shown rather than a room you are standing in.
 *
 * So: two gloved hands, matching the coat and rubber the rest of the crew can
 * see on you. The right one works — it holds whatever tool the thing you are
 * breaking into needs, and it visibly pries, picks, cuts and drills. The left
 * one carries: the last thing you took is in it, raised and turned over once
 * when you take it, then carried at your side until you bank it or drop it.
 *
 * Everything here is authored in CAMERA SPACE, so the hands are children of a
 * root that never moves and the illusion of a body is entirely animation:
 * bob from the gait, sway lagging the mouse, a dip on landing, arms tucking in
 * on a sprint. None of it is physical and all of it is what sells it.
 */

// --- what the session tells us each frame -----------------------------------

/**
 * What the right hand is doing to a fixture. A translation of the interaction
 * layer's `WorkKind` rather than the thing itself, so the hands do not have to
 * import the audio cue table to know how to move.
 */
export type HandWork = 'pry' | 'pick' | 'card' | 'cut' | 'drill' | 'grab' | 'give';

/** A one-off flourish, fired rather than sampled. */
export type Gesture = 'shove' | 'use' | 'stow';

export interface ViewmodelState {
  /** False on the results screen, while paused, and when you are out. */
  visible: boolean;
  /** Ground speed, m/s. Drives the gait. */
  speed: number;
  grounded: boolean;
  sprinting: boolean;
  crouching: boolean;
  /** This frame's mouse motion, radians. The hands lag it. */
  yawDelta: number;
  pitchDelta: number;
  /** In the right hand. Null for empty hands. */
  tool: ItemId | null;
  /** In the left hand — the most recent thing you took. */
  loot: ItemId | null;
  /** Non-null only while a hold is actually progressing. */
  work: HandWork | null;
  /** 0..1 through the current hold. */
  progress: number;
  /** How lit you are, 0..1. The hands are lit by the room, not by a headlamp. */
  light: number;
}

/** Which tool each job wants in the hand, regardless of what else you carry. */
const WORK_TOOL: Record<HandWork, ItemId | null> = {
  pry: 'crowbar',
  pick: 'lockpick',
  card: 'keycard',
  cut: 'grinder',
  drill: 'drill',
  grab: null,
  give: null,
};

// --- geometry ---------------------------------------------------------------

/**
 * The hands are lighter than the thief they belong to, on purpose.
 *
 * The crew's coat is 0x24201a and the gloves are 0x24262a, which is right for a
 * figure read across a moonlit atrium — at that distance the silhouette is the
 * whole character and any more value in it turns them into a target. At forty
 * centimetres the same albedo is a black hole: 0.018 linear against this
 * exposure has nowhere to go, the flat-shaded facets all round to the same
 * value, and the first pass of this viewmodel photographed as two dark wedges
 * with a crowbar-shaped absence between them. These are the same hues at
 * roughly twice the value, which reads as the same costume up close.
 */
const HAND = {
  glove: new Color(0x3c4048),
  /** The scuffed patch across the knuckles. Lighter than the glove, not darker. */
  gloveWorn: new Color(0x51565f),
  cuff: new Color(0x3a342b),
  strap: new Color(0x6e6551),
};

/**
 * A hand, wrist-first.
 *
 * The origin is the wrist, fingers run down -Z, the back of the hand faces +Y
 * and the thumb sits on +X for a right hand. `side` is -1 for the left, which
 * mirrors by negating X on every part rather than by scaling the group: a
 * negative scale flips triangle winding, and flat-shaded low-poly with inverted
 * normals turns into a black glove-shaped hole.
 *
 * Fingers are two jointed segments each, which is the whole reason they are
 * built rather than merged. A fist around a crowbar shaft, a flat palm under a
 * keycard and open fingers reaching for a shelf are the same fourteen boxes at
 * three different rotations, and that is what makes holding thirty-seven
 * different objects look deliberate instead of magnetic.
 */
class Hand {
  readonly root = new Group();
  /** Where a held object is parented. Sits in the palm, not at the wrist. */
  readonly grip = new Group();
  private readonly knuckles: Group[] = [];
  private readonly tips: Group[] = [];
  private readonly thumbBase = new Group();
  private readonly thumbTip = new Group();

  constructor(side: 1 | -1) {
    const glove = surface('vmGlove', HAND.glove, { roughness: 0.97 });
    const gloveWorn = surface('vmGloveWorn', HAND.gloveWorn, { roughness: 0.88 });
    const cuff = surface('vmCuff', HAND.cuff, { roughness: 0.96 });
    const strap = surface('vmStrap', HAND.strap, { roughness: 0.9 });

    /**
     * Forearm — short, and thinner than a forearm.
     *
     * The first build ran it a full thirty-four centimetres back from the wrist,
     * on the reasoning that an arm which ends in mid-air is worse than one that
     * runs off the edge. It does run off an edge: the near one. At that length
     * the elbow ends up fifteen centimetres from the eye, where an eleven-
     * centimetre cylinder foreshortens into a slab a third of the screen wide,
     * and the photograph is two brown wedges with a pair of gloves lost between
     * them. Twenty-four centimetres leaves the frame at the bottom corner
     * instead, which is where an arm is supposed to leave it.
     */
    const fore = piece(new CylinderGeometry(0.037, 0.046, 0.24, 7), cuff, 0.72);
    fore.rotation.x = Math.PI / 2;
    fore.position.set(side * 0.004, 0.004, 0.145);
    this.root.add(fore);

    // The cuff band. Reads as the join between sleeve and glove, and it is the
    // part that catches a rim light when you turn under a fitting.
    const band = piece(new CylinderGeometry(0.047, 0.045, 0.035, 8), strap, 0.95);
    band.rotation.x = Math.PI / 2;
    band.position.z = 0.032;
    this.root.add(band);

    // Palm, slightly wedge-shaped: thicker at the wrist, tapering to the knuckles.
    const palm = piece(new BoxGeometry(0.083, 0.036, 0.098), glove, 1);
    palm.position.set(side * 0.002, 0, -0.048);
    this.root.add(palm);

    // A worn patch across the back of the hand. Three millimetres of a second
    // colour, and it is the difference between a glove and a grey box.
    const backing = piece(new BoxGeometry(0.07, 0.008, 0.062), gloveWorn, 1);
    backing.position.set(side * 0.002, 0.019, -0.05);
    this.root.add(backing);

    /**
     * Four fingers, with air between them.
     *
     * The first build pitched them 20.5mm apart and made them 18.5mm wide, which
     * leaves a two-millimetre gap — under a single key light, four flat-shaded
     * boxes two millimetres apart in the same plane are one box. They also all
     * carried the same value, so nothing separated them even where the geometry
     * did. Six millimetres of gap and alternating values fixes both: the hand
     * reads as fingers at forty centimetres instead of a mitten.
     */
    for (let i = 0; i < 4; i++) {
      const base = new Group();
      base.position.set(side * (0.0335 - i * 0.0235), -0.002, -0.096);
      // Index longest through to little finger shortest, and the little finger
      // set slightly lower, which is what a real hand does at rest.
      const len = 0.044 - Math.abs(i - 0.7) * 0.004;
      base.position.y -= i * 0.0025;
      // A few degrees of splay per finger, fanning away from the middle two.
      base.rotation.y = side * (i - 1.5) * 0.055;
      const shade = i % 2 === 0 ? 0.98 : 0.86;
      const prox = piece(new BoxGeometry(0.0175, 0.021, len), glove, shade * 0.96);
      prox.position.z = -len / 2;
      base.add(prox);
      // A knuckle, proud of the finger, so the closed fist has a ridge along it
      // instead of a flat front face.
      const knuck = piece(new BoxGeometry(0.019, 0.0125, 0.016), gloveWorn, shade);
      knuck.position.set(0, 0.008, -0.008);
      base.add(knuck);

      const tip = new Group();
      tip.position.z = -len;
      const dist = piece(new BoxGeometry(0.0165, 0.0185, len * 0.82), glove, shade);
      dist.position.z = (-len * 0.82) / 2;
      tip.add(dist);
      base.add(tip);

      this.root.add(base);
      this.knuckles.push(base);
      this.tips.push(tip);
    }

    // Thumb, off the side of the palm and rotated out of its plane.
    this.thumbBase.position.set(side * 0.041, -0.006, -0.03);
    this.thumbBase.rotation.set(0, side * -0.5, side * -0.35);
    const tprox = piece(new BoxGeometry(0.023, 0.024, 0.04), glove, 0.94);
    tprox.position.z = -0.02;
    this.thumbBase.add(tprox);
    this.thumbTip.position.z = -0.04;
    const tdist = piece(new BoxGeometry(0.021, 0.021, 0.034), glove, 1);
    tdist.position.z = -0.017;
    this.thumbTip.add(tdist);
    this.thumbBase.add(this.thumbTip);
    this.root.add(this.thumbBase);

    this.grip.position.set(side * 0.004, 0.006, -0.088);
    this.root.add(this.grip);

    this.pose(0, 0);
  }

  /**
   * Close the hand. `curl` is 0 for flat and 1 for a fist; `wrap` is how far the
   * thumb comes across, which is separate because a hand holding a card is flat
   * with the thumb clamped and a hand reaching for a shelf is open with the
   * thumb out.
   */
  pose(curl: number, wrap = curl): void {
    for (let i = 0; i < 4; i++) {
      // Ring and little finger lead a closing hand by a few degrees. Fingers
      // that all arrive together read as one moulded part.
      const lead = 1 + i * 0.09;
      this.knuckles[i].rotation.x = -curl * 1.35 * lead;
      this.tips[i].rotation.x = -curl * 1.5 * lead;
    }
    this.thumbBase.rotation.x = -wrap * 0.5;
    this.thumbTip.rotation.x = -wrap * 0.9;
  }
}

/**
 * A flat-shaded part, darkened by a fixed amount.
 *
 * Not the world's contact shading. That writes a gradient in the geometry's own
 * Y, which is meaningful for a shelving unit standing on a floor and meaningless
 * for a forearm that is then rotated ninety degrees about X — the gradient ends
 * up running along the arm, and half of it goes dark for no reason anybody
 * looking at it could name. What a hand actually wants is the opposite: whole
 * parts a little darker than others, because a forearm is shaded by the body it
 * hangs off and a fingertip is not.
 *
 * `value` is a flat multiplier, 1 for untouched. `surface()` turns on
 * `vertexColors` for every standard material, so a geometry with no colour
 * attribute would multiply its albedo by zero — every part goes through here.
 */
function piece(geo: BufferGeometry, mat: Material, value: number): Mesh {
  const v = Math.round(clamp(value, 0, 1) * 255);
  tintVertexColor(geo, (v << 16) | (v << 8) | v);
  return new Mesh(geo, mat);
}

/**
 * Scales here are roughly two thirds life size, and that is deliberate.
 *
 * A 230mm angle grinder held at true scale forty centimetres from one eye at a
 * 60-degree field of view covers a third of the screen and hides the hand
 * holding it, which is why every shooter ever made shrinks its viewmodel. These
 * were authored at 1.0 and photographed as an orange wall and a yellow wall.
 */
// --- how each object sits in a fist -----------------------------------------

interface Grip {
  /** The point on the object, in its own floor-standing space, that the palm takes. */
  hold: [number, number, number];
  /** Applied after the object is moved onto the grip point. */
  rot: [number, number, number];
  scale: number;
  /** How closed the fist is around it. */
  curl: number;
}

/**
 * Hand-authored for the five tools, because those are the objects the player
 * looks at for minutes at a time, and because a crowbar held by its middle is
 * immediately, unfixably wrong.
 *
 * Loot is fitted automatically instead — see `fit`. Thirty-two grips tuned by
 * eye is a week of work whose entire payoff is that a tub of vitamins hangs at
 * a slightly better angle.
 */
const GRIPS: Partial<Record<ItemId, Grip>> = {
  // Held a third of the way along, claw forward and down, the way you carry one
  // when you are about to put it into something.
  /**
   * Negative yaw, unlike every other tool here, and that is not a typo.
   *
   * These bodies are authored lying on a floor with their long axis along X, and
   * a quarter turn about Y stands that axis up along the view. For the grinder,
   * the drill and the picks the business end is at +X, so the positive turn puts
   * the disc, the bit and the picks out in front where you can see them working.
   * The crowbar is the one built the other way round — its claw is at -X — so the
   * same positive turn buried the claw in the camera at seventeen centimetres and
   * threw the plain end of the shaft off the bottom-right corner. Held like this
   * the shaft runs away from the fist and the claw is the far tip, which is both
   * the readable silhouette and the end you are about to put in a shutter.
   */
  crowbar: { hold: [0.23, 0.03, 0], rot: [-0.22, -Math.PI / 2, 0], scale: 0.6, curl: 0.95 },
  // Pinched, not gripped. The roll sits in the palm and two fingers work.
  lockpick: { hold: [0.1, 0.04, 0], rot: [0.05, Math.PI / 2, 0.3], scale: 0.8, curl: 0.55 },
  // Body in the fist, disc out past the knuckles, guard uppermost.
  grinder: { hold: [-0.02, 0.165, 0], rot: [-0.12, Math.PI / 2, 0], scale: 0.6, curl: 0.95 },
  // Held flat between thumb and forefinger, offered forward.
  keycard: { hold: [0, 0.004, 0], rot: [0.35, Math.PI / 2, 0], scale: 1.0, curl: 0.18 },
  // Pistol grip, so the hand is under the body and the bit points away.
  drill: { hold: [-0.075, 0.06, 0], rot: [-0.14, Math.PI / 2, 0], scale: 0.58, curl: 0.95 },
};

// --- the viewmodel ----------------------------------------------------------

/** Frame-rate independent approach: how far to move toward a target this frame. */
function approach(dt: number, tau: number): number {
  return 1 - Math.exp(-dt / tau);
}

export class Viewmodel {
  readonly scene = new Scene();
  readonly camera: PerspectiveCamera;

  /** Everything that animates hangs off this. Camera space, camera at origin. */
  private readonly rig = new Group();
  private readonly right = new Hand(1);
  private readonly left = new Hand(-1);
  private readonly key: DirectionalLight;
  private readonly fill: DirectionalLight;
  private readonly ambient: HemisphereLight;

  /** Cached geometry per item, because switching tools must not allocate. */
  private readonly bodies = new Map<string, Object3D>();
  private heldTool: ItemId | null = null;
  private heldLoot: ItemId | null = null;

  // Animation state.
  private gait = 0;
  private swayX = 0;
  private swayY = 0;
  private lean = 0;
  private sprint = 0;
  private crouch = 0;
  private show = 0;
  private airborne = 0;
  private land = 0;
  private landVel = 0;
  private workT = 0;
  private workBlend = 0;
  private lastWork: HandWork | null = null;
  /** Seconds since the left hand was handed something new. */
  private lootAge = 99;
  private gestureKind: Gesture | null = null;
  private gestureT = 0;
  private lit = 0;

  private readonly warm = new Color(0xffd7ad);
  private readonly cool = new Color(0x8fa8cc);
  private readonly keyColor = new Color();

  constructor() {
    /**
     * Sixty degrees against the world's seventy-two.
     *
     * A viewmodel rendered at the world's field of view is correct and looks
     * wrong: at 72° a forearm reaching to the middle of the screen is stretched
     * hard toward the corner it leaves from, and the hand at the end of it
     * shears. Every first-person game solves this the same way, with a second
     * narrower camera, and the mismatch is invisible because there is nothing in
     * the frame to compare the hands against.
     */
    this.camera = new PerspectiveCamera(60, 1, 0.008, 4);
    this.scene.add(this.rig);
    this.rig.add(this.right.root, this.left.root);

    /**
     * A three-point rig, and no shadows.
     *
     * The hands cannot use the world's lighting: the moon is outside, the shop
     * fittings were baked into geometry that is not in this scene, and the four
     * realtime slots belong to torches. So they get their own key, from up and
     * to the left, which is where the mall's ceiling runs are; a cold fill from
     * below-right so the undersides do not go to black; and a hemisphere for
     * the rest. All three are scaled by how lit the player actually is, which is
     * the same number the concealment mechanic reads — stand in a dark corridor
     * and your own hands go dim, walk under a lit shopfront and they come back.
     * It costs nothing and it is the thing that stops them looking pasted on.
     */
    this.key = new DirectionalLight(0xffffff, 1);
    this.key.position.set(-0.55, 1, 0.5);
    this.fill = new DirectionalLight(0x9fb8dc, 0.4);
    this.fill.position.set(0.9, -0.25, 0.35);
    this.ambient = new HemisphereLight(0x7d8ea6, 0x22262c, 0.6);
    this.scene.add(this.key, this.fill, this.ambient);
  }

  /**
   * Hands off, immediately.
   *
   * `update` fades them for the pause card, which is right — a menu should not
   * amputate you. Handing the camera to the review tool or the screenshot
   * harness is different: nothing is driving the animation any more, so a fade
   * would freeze halfway, and every art reference shot would have a pair of
   * gloves in the corner of it.
   */
  setActive(on: boolean): void {
    if (on) return;
    this.show = 0;
    this.rig.visible = false;
  }

  /**
   * Borrow the world's environment map rather than generating a second one.
   *
   * It is the same night sky either way, and a second PMREM pass costs a frame
   * at boot for an identical result. Without any environment every metal part of
   * every tool renders black — a standard material at metalness 0.72 has almost
   * no diffuse term and nothing but the environment to fill the specular one, so
   * a chrome drill chuck and a bare-steel claw both come out as object-shaped
   * holes in the frame.
   */
  useEnvironment(tex: Texture | null): void {
    this.scene.environment = tex;
  }

  /** Fire a one-off. Retriggering restarts it; these are all under a second. */
  gesture(kind: Gesture): void {
    this.gestureKind = kind;
    this.gestureT = 0;
  }

  update(dt: number, s: ViewmodelState): void {
    dt = Math.min(dt, 0.05);

    // --- what is in each hand ---------------------------------------------
    // The job wins over the loadout: holding a crowbar and standing at a keypad
    // should show the keycard, because that is what is about to be used.
    const want = (s.work && WORK_TOOL[s.work]) ?? s.tool;
    if (want !== this.heldTool) {
      this.heldTool = want;
      this.equip(this.right, want, 0.34);
    }
    if (s.loot !== this.heldLoot) {
      // Only a genuinely new object restarts the flourish. Banking the top item
      // of a stack of three uncovers the next one and that is a swap, not a
      // find, so it gets the same animation but the player reads it correctly
      // because the object in the hand changed.
      this.heldLoot = s.loot;
      this.lootAge = 0;
      this.equip(this.left, s.loot, 0.26);
    } else {
      this.lootAge += dt;
    }

    // --- gait ---------------------------------------------------------------
    // Phase advances with DISTANCE, not time, so the bob stays locked to the
    // footsteps at every speed and stops dead the instant you do.
    this.gait += s.speed * dt * 3.1;
    const stride = Math.min(1, s.speed / 4.4);
    this.sprint += (Number(s.sprinting && s.speed > 3) - this.sprint) * approach(dt, 0.14);
    this.crouch += (Number(s.crouching) - this.crouch) * approach(dt, 0.1);
    this.show += (Number(s.visible) - this.show) * approach(dt, 0.11);
    this.rig.visible = this.show > 0.02;
    if (!this.rig.visible) return;

    // --- landing ------------------------------------------------------------
    if (!s.grounded) this.airborne += dt;
    else if (this.airborne > 0) {
      // A spring rather than a curve, because the recovery is the readable part:
      // the drop is a single frame of falling weight and the settle afterwards
      // is what tells you how far you fell.
      this.landVel -= Math.min(1, this.airborne * 3.4);
      this.airborne = 0;
    }
    this.landVel += (0 - this.land) * 260 * dt;
    this.landVel *= Math.exp(-13 * dt);
    this.land += this.landVel * dt;

    // --- sway ---------------------------------------------------------------
    // The hands lag the head. Turn left and they trail right, then catch up.
    // Rate rather than per-frame delta, or the amount of sway would depend on
    // the frame rate, which is exactly the sort of thing nobody sees and
    // everybody feels.
    const yawRate = s.yawDelta / Math.max(dt, 1e-4);
    const pitchRate = s.pitchDelta / Math.max(dt, 1e-4);
    const k = approach(dt, 0.075);
    this.swayX += (clamp(-yawRate * 0.010, -0.055, 0.055) - this.swayX) * k;
    this.swayY += (clamp(-pitchRate * 0.008, -0.04, 0.04) - this.swayY) * k;
    this.lean += (clamp(-yawRate * 0.05, -0.16, 0.16) - this.lean) * approach(dt, 0.1);

    // --- the rig ------------------------------------------------------------
    const bobAmp = stride * (0.65 + this.sprint * 0.5);
    const bx = Math.sin(this.gait) * 0.021 * bobAmp;
    // Twice the frequency on the vertical, because both feet land per cycle.
    const by = -Math.abs(Math.cos(this.gait)) * 0.017 * bobAmp;
    this.rig.position.set(
      this.swayX + bx,
      this.swayY + by + this.land - this.crouch * 0.04 - (1 - this.show) * 0.55,
      this.sprint * 0.05,
    );
    this.rig.rotation.set(
      this.swayY * -1.6 + this.sprint * 0.26,
      this.lean * 0.9,
      this.lean * 1.5 + Math.sin(this.gait) * 0.016 * bobAmp,
    );

    // --- lighting -----------------------------------------------------------
    // Eased, so walking out of a doorway is a change in the light rather than a
    // cut. The floor is deliberately not zero: hands that vanish entirely in the
    // dark leave the player back where they started, holding nothing.
    this.lit += (clamp(s.light, 0, 1) - this.lit) * approach(dt, 0.22);
    this.keyColor.copy(this.cool).lerp(this.warm, this.lit);
    this.key.color.copy(this.keyColor);
    // Half what the first correction reached for. That pass was fixing hands so
    // dark they photographed as an absence, and it overshot into the opposite
    // failure: gloves brighter than the moonlit floor behind them, reading as
    // pale rubber mittens pasted over the frame. A glove is a dark object. It
    // should sit a stop or two under the tiles it is lit by, and only the worn
    // knuckle patch and the strap should catch anything.
    this.key.intensity = 0.6 + this.lit * 1.15;
    this.fill.intensity = 0.2 + this.lit * 0.3;
    this.ambient.intensity = 0.32 + this.lit * 0.4;
    // Reflections. Without this every metal part of every tool is black: a
    // standard material at metalness 0.72 has almost no diffuse term and nothing
    // but the environment to put in the specular one, so a chrome drill chuck
    // and a bare-steel claw both render as an object-shaped hole. Higher than
    // the mall's own 0.16 because that number is held down by rooms three floors
    // inside a building the sky cannot reach, and a hand is never in one.
    this.scene.environmentIntensity = 0.24 + this.lit * 0.55;

    // --- poses --------------------------------------------------------------
    this.poseRight(dt, s);
    this.poseLeft(dt);
  }

  // ------------------------------------------------------------------------

  /** The working hand: rest, or driven by whatever it is breaking into. */
  private poseRight(dt: number, s: ViewmodelState): void {
    const h = this.right.root;
    const g = this.heldTool ? GRIPS[this.heldTool] : undefined;

    // Rest: down and to the right, angled in across the body. Sprinting pulls it
    // in and down and turns the wrist over, which is how you carry a heavy thing
    // while running rather than how you present it.
    let px = 0.235 - this.sprint * 0.025;
    let py = -0.205 - this.sprint * 0.1;
    let pz = -0.52 + this.sprint * 0.14;
    let rx = 0.1 + this.sprint * 0.42;
    let ry = 0.34 + this.sprint * 0.2;
    let rz = -0.35 - this.sprint * 0.4;
    let curl = this.heldTool ? (g?.curl ?? 0.85) : 0.28;

    const active = s.work !== null;
    if (s.work && s.work !== this.lastWork) {
      this.lastWork = s.work;
      this.workT = 0;
    }
    if (active) this.workT += dt;
    this.workBlend += (Number(active) - this.workBlend) * approach(dt, 0.12);

    if (this.workBlend > 0.004) {
      const t = this.workT;
      // Every job is the same shape — reach in, do the thing, hold — and differs
      // in what "the thing" is. Written as an offset from rest and blended in,
      // so a hold that is cancelled halfway retreats rather than snapping.
      let ox = -0.11;
      let oy = 0.11;
      let oz = -0.12;
      let orx = 0.24;
      let ory = 0.3;
      let orz = -0.12;

      switch (s.work) {
        case 'pry': {
          // Two-beat: haul back against the bar, then it gives a little. The
          // cycle is deliberately slow — a crowbar is leverage, not speed.
          const c = (t % 1.05) / 1.05;
          const heave = c < 0.55 ? ease(c / 0.55) : 1 - ease((c - 0.55) / 0.45);
          oz += heave * 0.06;
          oy += heave * 0.05;
          orz -= heave * 0.55;
          orx -= heave * 0.3;
          // The bar judders under load near the top of each heave.
          ox += Math.sin(t * 41) * 0.004 * heave;
          break;
        }
        case 'pick': {
          // Almost still. A wrist turning a tension wrench a few degrees at a
          // time, with the occasional slip back to zero.
          orz += Math.sin(t * 3.1) * 0.16 + Math.sin(t * 7.7) * 0.03;
          oy += Math.sin(t * 2.3) * 0.006;
          ox += Math.sin(t * 11.3) * 0.0015;
          break;
        }
        case 'card': {
          // One swipe per second and a pause, which is what people actually do
          // when a reader is not co-operating.
          const c = (t % 1.2) / 1.2;
          const swipe = c < 0.35 ? ease(c / 0.35) : c < 0.5 ? 1 : 1 - ease((c - 0.5) / 0.5);
          oy -= swipe * 0.12;
          oz -= swipe * 0.03;
          break;
        }
        case 'cut': {
          // Held into the work with both the vibration of the disc and a slow
          // sweep along the cut. The vibration is small on purpose: big shake is
          // a screen effect, small shake is a tool.
          oz -= 0.045;
          ox += Math.sin(t * 0.9) * 0.05;
          oy += Math.cos(t * 0.9) * 0.02;
          ox += Math.sin(t * 63) * 0.0045;
          oy += Math.cos(t * 71) * 0.0035;
          orz += Math.sin(t * 57) * 0.02;
          break;
        }
        case 'drill': {
          // Leaning weight onto it. Creeps forward as the hole deepens and
          // snatches when the bit bites.
          oz -= 0.05 + s.progress * 0.035;
          orx += 0.1;
          const bite = Math.sin(t * 5.3) > 0.7 ? 1 : 0;
          ox += Math.sin(t * 84) * 0.003 * (1 + bite * 2);
          oy += Math.cos(t * 79) * 0.003 * (1 + bite * 2);
          break;
        }
        case 'give': {
          // Putting something down. Open hand, presented, held there.
          oz -= 0.06;
          oy -= 0.04;
          curl = 0.12;
          break;
        }
        default: {
          // Reaching for a thing on a shelf: hand out, fingers open, close as
          // the take completes.
          oz -= 0.09;
          ox += 0.03;
          curl = 0.1 + ease(s.progress) * 0.7;
          break;
        }
      }

      const b = this.workBlend;
      px += ox * b;
      py += oy * b;
      pz += oz * b;
      rx += orx * b;
      ry += ory * b;
      rz += orz * b;
    }

    // Shove: a straight jab with an open palm, over in a third of a second.
    if (this.gestureKind === 'shove') {
      this.gestureT += dt;
      const c = this.gestureT / 0.34;
      if (c >= 1) this.gestureKind = null;
      else {
        const punch = c < 0.3 ? ease(c / 0.3) : 1 - ease((c - 0.3) / 0.7);
        pz -= punch * 0.34;
        px -= punch * 0.1;
        py += punch * 0.1;
        ry += punch * 0.4;
        rz -= punch * 0.3;
        curl = Math.max(curl - punch * 0.8, 0.05);
      }
    }

    h.position.set(px, py, pz);
    h.rotation.set(rx, ry, rz);
    this.right.pose(curl, curl * 0.9);
  }

  /**
   * The carrying hand.
   *
   * Nothing here is a mechanic. It exists so that taking something is an event
   * you watch happen: the hand comes up out of frame with the object in it,
   * turns it over once so you can see what it is, and lowers it to your side.
   * Two seconds, and it is the only feedback in the game that tells you what you
   * just picked up without reading anything.
   */
  private poseLeft(dt: number): void {
    const h = this.left.root;
    const carrying = this.heldLoot !== null;

    // Rest, empty: relaxed and low, mostly out of frame.
    let px = -0.275 - this.sprint * 0.02;
    let py = -0.25 - this.sprint * 0.1;
    let pz = -0.48 + this.sprint * 0.13;
    let rx = 0.08 + this.sprint * 0.4;
    let ry = -0.33 - this.sprint * 0.18;
    let rz = 0.32 + this.sprint * 0.4;
    let curl = 0.3;

    if (carrying) {
      // Carry pose: held in front of the hip, tilted up a few degrees so the
      // object's face is toward the player rather than the floor.
      px = -0.245 - this.sprint * 0.03;
      py = -0.215 - this.sprint * 0.11;
      pz = -0.54 + this.sprint * 0.15;
      rx = 0.2 + this.sprint * 0.4;
      ry = -0.28 - this.sprint * 0.16;
      rz = 0.36 + this.sprint * 0.4;
      curl = 0.8;

      const t = this.lootAge;
      if (t < 1.9) {
        // In from below, overshoot, settle. The hold in the middle is where the
        // player actually reads the object, so it is the longest part.
        const rise = t < 0.34 ? ease(t / 0.34) : 1;
        const drop = t < 1.15 ? 0 : ease((t - 1.15) / 0.75);
        const up = rise - drop;
        // Barely higher, and steeply raked.
        //
        // The obvious way to present an object is to lift the arm, and it is
        // wrong: lifting puts the hand in the middle of the frame with the
        // forearm running back down to an elbow beside the lens, so the widest,
        // nearest, darkest part of the whole rig is drawn diagonally across the
        // shot and the object being presented is the small thing behind it. What
        // works is pitching the wrist hard instead — the object comes up over
        // the knuckles into the lower-left quadrant while the forearm dives out
        // through the bottom edge and is never seen at all.
        px += up * 0.095;
        py += up * 0.085;
        pz += up * 0.04;
        rx += up * 0.62;
        rz -= up * 0.11;
        // The turn goes on the object, not on the arm. Yawing the wrist swings
        // the elbow the other way, and for this hand outward is negative, so
        // adding to it drove the forearm straight across the camera.
        ry -= up * 0.1;
        this.left.grip.rotation.y = up * (0.4 + Math.sin(Math.min(t, 1.5) * 2.1) * 0.9);
      } else {
        this.left.grip.rotation.y = 0;
      }
    } else {
      this.left.grip.rotation.y = 0;
    }

    // Drinking or swallowing something: the left hand brings it up to the mouth
    // and out of the top of the frame.
    if (this.gestureKind === 'use') {
      this.gestureT += dt;
      const c = this.gestureT / 0.9;
      if (c >= 1) this.gestureKind = null;
      else {
        const lift = c < 0.35 ? ease(c / 0.35) : c < 0.62 ? 1 : 1 - ease((c - 0.62) / 0.38);
        py += lift * 0.3;
        px += lift * 0.14;
        pz -= lift * 0.1;
        rx += lift * 0.9;
        rz += lift * 0.5;
      }
    }

    h.position.set(px, py, pz);
    h.rotation.set(rx, ry, rz);
    this.left.pose(curl, curl);
  }

  // ------------------------------------------------------------------------

  /**
   * Put an object in a hand, or empty it.
   *
   * The geometry is cached and reparented rather than rebuilt, because switching
   * between two tools is something a player does dozens of times a round and
   * building a crowbar allocates four buffers and two materials.
   */
  private equip(hand: Hand, id: ItemId | null, maxDim: number): void {
    for (let i = hand.grip.children.length - 1; i >= 0; i--) {
      hand.grip.remove(hand.grip.children[i]);
    }
    if (!id) return;
    const key = `${id}:${maxDim}`;
    let node = this.bodies.get(key);
    if (!node) {
      node = this.mount(id, maxDim);
      this.bodies.set(key, node);
    }
    hand.grip.add(node);
  }

  /** Build the object and work out where in the hand it belongs. */
  private mount(id: ItemId, maxDim: number): Object3D {
    const holder = new Group();
    holder.name = `held:${id}`;
    const body = lootBody(id);
    const grip = GRIPS[id];

    if (grip) {
      body.position.set(-grip.hold[0], -grip.hold[1], -grip.hold[2]);
      holder.rotation.set(grip.rot[0], grip.rot[1], grip.rot[2]);
      holder.scale.setScalar(grip.scale);
      holder.add(body);
      return holder;
    }

    /**
     * No authored grip, so measure it.
     *
     * Loot is authored at real-world size — a television is ninety centimetres
     * across — and real-world size at arm's length fills the screen. Everything
     * without a grip is centred on the palm and capped, which keeps a ring
     * ring-sized and stops a stolen TV from becoming the level.
     */
    const box = boundsOf(body);
    const size = new Vector3().subVectors(box.max, box.min);
    const largest = Math.max(size.x, size.y, size.z, 1e-3);
    const scale = Math.min(1, maxDim / largest);
    body.position.set(
      -(box.min.x + box.max.x) / 2,
      -(box.min.y + box.max.y) / 2,
      -(box.min.z + box.max.z) / 2,
    );
    holder.add(body);
    holder.scale.setScalar(scale);
    // Long axis across the fist, tipped so it faces the player rather than the
    // ceiling. The same two numbers for everything, which is fine: an object
    // held in a closed hand only has to look held.
    holder.rotation.set(0.24, Math.PI / 2, 0.1);
    holder.position.set(0, 0.012 + (size.y * scale) / 2, -0.01);
    return holder;
  }
}

// --- helpers ----------------------------------------------------------------

function clamp(v: number, lo: number, hi: number): number {
  return v < lo ? lo : v > hi ? hi : v;
}

/** Smoothstep. Every animation here eases in and out; none of them are linear. */
function ease(t: number): number {
  const c = clamp(t, 0, 1);
  return c * c * (3 - 2 * c);
}

/**
 * Local bounds of a subtree, without three's Box3.setFromObject.
 *
 * That helper needs world matrices to be current, and these bodies have never
 * been in a scene — it would measure them all at the origin at unit scale and
 * a television would come back the size of a ring.
 */
function boundsOf(root: Object3D): { min: Vector3; max: Vector3 } {
  const min = new Vector3(Infinity, Infinity, Infinity);
  const max = new Vector3(-Infinity, -Infinity, -Infinity);
  const v = new Vector3();
  root.updateMatrixWorld(true);
  root.traverse((o) => {
    const mesh = o as Mesh;
    if (!mesh.isMesh) return;
    const pos = mesh.geometry.getAttribute('position');
    if (!pos) return;
    for (let i = 0; i < pos.count; i++) {
      v.fromBufferAttribute(pos, i).applyMatrix4(mesh.matrixWorld);
      min.min(v);
      max.max(v);
    }
  });
  if (!Number.isFinite(min.x)) {
    min.set(0, 0, 0);
    max.set(0.1, 0.1, 0.1);
  }
  return { min, max };
}
